import EmptyContainer from '../../../../shared/empty-container';
import {
    LeoSubscriptionFlattenedRow,
} from '../../../../types/subscriptions';
import {
    formatSubscriptionAmount,
    formatSubscriptionDate,
    formatSubscriptionInterval,
    formatSubscriptionStatus,
} from '../utils';

const getStatusClasses = (status?: string | null) => {
    if (!status) {
        return 'border-gray-200 bg-gray-100 text-gray-600 dark:border-white/10 dark:bg-white/10 dark:text-gray-200';
    }

    if (status.toLowerCase() === 'active') {
        return 'border-[#dbe9d6] bg-[#f3fded] text-[#00330F] dark:border-[#274330] dark:bg-[#102515] dark:text-[#dff6d4]';
    }

    return 'border-[#ece3c1] bg-[#fffaf0] text-[#6d5800] dark:border-[#5b4f2b] dark:bg-[#2b2417] dark:text-[#f5e0a8]';
};

const getScopeClasses = (scope: LeoSubscriptionFlattenedRow['scope']) => {
    if (scope === 'Property') {
        return 'border-[#dbe9d6] bg-[#f3fded] text-[#00330F] dark:border-[#274330] dark:bg-[#102515] dark:text-[#dff6d4]';
    }

    return 'border-gray-200 bg-white text-gray-700 dark:border-white/10 dark:bg-white/5 dark:text-gray-200';
};

const getRowClasses = (row: LeoSubscriptionFlattenedRow) => {
    if (row.highlightRenewal) {
        return 'bg-[#fffaf0] dark:bg-[#2b2417]';
    }

    return 'bg-white dark:bg-black';
};

const SubscriptionsTable = ({
    rows,
    loading,
}: {
    rows: LeoSubscriptionFlattenedRow[];
    loading?: boolean;
}) => {
    if (loading) {
        return (
            <div className='rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-black p-4'>
                <div className='space-y-3 animate-pulse'>
                    {Array.from({ length: 6 }).map((_, index) => (
                        <div
                            key={index}
                            className='h-14 rounded-2xl bg-gray-100 dark:bg-white/10'
                        />
                    ))}
                </div>
            </div>
        );
    }

    if (!rows.length) {
        return (
            <div className='rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-black p-4'>
                <EmptyContainer
                    text='No subscriptions matched the current filters.'
                    showImage
                    containerStyle='min-h-[280px]'
                />
            </div>
        );
    }

    return (
        <div className='overflow-hidden rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-black shadow-sm'>
            <div className='overflow-x-auto'>
                <table className='min-w-full text-left'>
                    <thead className='bg-[#FAFAFA] dark:bg-white/5'>
                        <tr>
                            <th className='px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300'>
                                Scope
                            </th>
                            <th className='px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300'>
                                Account
                            </th>
                            <th className='px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300'>
                                Client / Property
                            </th>
                            <th className='px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300'>
                                Scenario
                            </th>
                            <th className='px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300'>
                                Plan
                            </th>
                            <th className='px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300'>
                                Renewal
                            </th>
                            <th className='px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300'>
                                Billing
                            </th>
                            <th className='px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300'>
                                Status
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr
                                key={row.id}
                                className={`border-t border-gray-100 dark:border-white/10 align-top ${getRowClasses(row)}`}
                            >
                                <td className='px-4 py-4'>
                                    <span
                                        className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${getScopeClasses(
                                            row.scope,
                                        )}`}
                                    >
                                        {row.scope}
                                    </span>
                                </td>
                                <td className='px-4 py-4'>
                                    <div className='min-w-[180px]'>
                                        <p className='text-sm font-semibold text-[#101010] dark:text-primary-darkMode'>
                                            {row.userName || 'Unknown user'}
                                        </p>
                                        <p className='mt-1 text-xs text-gray-500 dark:text-gray-400 break-all'>
                                            {row.userEmail || 'No email'}
                                        </p>
                                    </div>
                                </td>
                                <td className='px-4 py-4'>
                                    <div className='min-w-[200px]'>
                                        <p className='text-sm font-semibold text-[#101010] dark:text-primary-darkMode'>
                                            {row.propertyName || 'Unknown property'}
                                        </p>
                                        <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                                            {row.clientName || 'No client assigned'}
                                        </p>
                                    </div>
                                </td>
                                <td className='px-4 py-4'>
                                    <div className='min-w-[160px]'>
                                        <p className='text-sm text-[#101010] dark:text-primary-darkMode'>
                                            {row.scenarioName || 'Primary subscription'}
                                        </p>
                                    </div>
                                </td>
                                <td className='px-4 py-4'>
                                    <div className='min-w-[220px]'>
                                        <p className='text-sm font-medium text-[#101010] dark:text-primary-darkMode'>
                                            {row.planName || 'No plan'}
                                        </p>
                                        <p className='mt-1 text-xs text-gray-500 dark:text-gray-400 break-all'>
                                            {row.subscriptionId || 'No subscription ID'}
                                        </p>
                                    </div>
                                </td>
                                <td className='px-4 py-4'>
                                    <div className='min-w-[180px]'>
                                        <p className='text-sm font-medium text-[#101010] dark:text-primary-darkMode'>
                                            {formatSubscriptionDate(
                                                row.subscriptionRenewalDate,
                                            )}
                                        </p>
                                        <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                                            {row.daysUntilRenewal == null
                                                ? 'No renewal window'
                                                : `${row.daysUntilRenewal} day${
                                                      row.daysUntilRenewal === 1
                                                          ? ''
                                                          : 's'
                                                  } remaining`}
                                        </p>
                                        {row.conditionalFormatting?.reason ? (
                                            <p className='mt-2 text-xs font-medium text-[#6d5800] dark:text-[#f5e0a8]'>
                                                {row.conditionalFormatting.reason}
                                            </p>
                                        ) : null}
                                    </div>
                                </td>
                                <td className='px-4 py-4'>
                                    <div className='min-w-[140px]'>
                                        <p className='text-sm font-medium text-[#101010] dark:text-primary-darkMode'>
                                            {formatSubscriptionAmount(
                                                row.amount,
                                                row.currency,
                                            )}
                                        </p>
                                        <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                                            {formatSubscriptionInterval(
                                                row.interval,
                                            )}
                                        </p>
                                    </div>
                                </td>
                                <td className='px-4 py-4'>
                                    <span
                                        className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusClasses(
                                            row.subscriptionStatus,
                                        )}`}
                                    >
                                        {formatSubscriptionStatus(
                                            row.subscriptionStatus,
                                        )}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SubscriptionsTable;
